import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { Modal } from '../../components/Modal';
import { showErrorNotification, useErrorNotification } from '../../hooks/useErrorNotification';
import {
  type QuestionDetail,
  type QuestionDetailResponse,
  type QuestionListItem,
  type QuestionsListResponse,
  type StatusMessage,
} from './types';
import { formatDateTime } from './utils';

type QuestionDetailModalProps = {
  detail: QuestionDetail;
  loading: boolean;
  updating: boolean;
  error: string | null;
  onClose: () => void;
  onToggleRecycle: (question: QuestionDetail, desired: boolean) => void;
};

export function QuestionsManagement() {
  const { authedRequest } = useAuth();
  const [questions, setQuestions] = useState<QuestionListItem[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [detailModal, setDetailModal] = useState<{
    questionId: string;
    loading: boolean;
    updating: boolean;
    data: QuestionDetail | null;
    error: string | null;
  } | null>(null);

  useErrorNotification(status, { header: 'Question management error' });

  const loadQuestions = useCallback(
    async (nextPage: number) => {
      setLoading(true);
      setStatus(null);
      try {
        const data = await authedRequest<QuestionsListResponse>(
          `/api/admin/questions?page=${nextPage}&pageSize=${pageSize}`
        );
        setQuestions(data.items);
        setTotalItems(data.totalItems);
      } catch (err: any) {
        setStatus({ type: 'err', text: err?.message || 'Failed to load questions.' });
      } finally {
        setLoading(false);
      }
    },
    [authedRequest]
  );

  useEffect(() => {
    loadQuestions(page);
  }, [loadQuestions, page]);

  const viewDetails = async (id: string) => {
    setDetailModal({ questionId: id, loading: true, updating: false, data: null, error: null });
    try {
      const data = await authedRequest<QuestionDetailResponse>(`/api/admin/questions/${id}`);
      setDetailModal({ questionId: id, loading: false, updating: false, data: data.question, error: null });
    } catch (err: any) {
      const message = err?.message || 'Unable to load question.';
      setDetailModal({ questionId: id, loading: false, updating: false, data: null, error: message });
      showErrorNotification(message, { header: 'Question detail error' });
    }
  };

  const toggleRecycle = async (question: QuestionDetail, desired: boolean) => {
    setDetailModal((prev) => (prev ? { ...prev, updating: true, error: null } : prev));
    try {
      const response = await authedRequest<{ question: QuestionListItem }>(`/api/admin/questions/${question.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ notAllowRecycle: desired }),
      });
      const nextFlag = response.question.notAllowRecycle;
      setQuestions((prev) => prev.map((item) => (item.id === question.id ? { ...item, notAllowRecycle: nextFlag } : item)));
      setDetailModal((prev) =>
        prev && prev.data
          ? { ...prev, updating: false, data: { ...prev.data, notAllowRecycle: nextFlag } }
          : prev
      );
      setStatus({
        type: 'ok',
        text: nextFlag ? 'Question recycling disabled for this item.' : 'Question can now be recycled.',
      });
    } catch (err: any) {
      const message = err?.message || 'Unable to update question.';
      setDetailModal((prev) => (prev ? { ...prev, updating: false, error: message } : prev));
      setStatus({ type: 'err', text: message });
    }
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / pageSize)), [totalItems, pageSize]);
  const hasItems = totalItems > 0;

  return (
    <div className="card card-outline card-secondary">
      <div className="card-header border-0">
        <h3 className="card-title mb-0">Question Management</h3>
        <p className="text-muted mb-0">Inspect generated questions and review their details.</p>
      </div>
      <div className="card-body">
        {status && status.type !== 'err' && (
          <div className={`alert alert-${status.type === 'ok' ? 'success' : 'info'} alert-dismissible fade show`} role="status">
            {status.text}
            <button type="button" className="btn-close" onClick={() => setStatus(null)} aria-label="Dismiss" />
          </div>
        )}
        <div className="table-responsive">
          <table className="table table-striped table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Generated</th>
                <th>Difficulty</th>
                <th>Topic</th>
                <th>Recycle</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((question) => (
                <tr key={question.id}>
                  <td>{formatDateTime(question.created_at)}</td>
                  <td>{question.difficulty ?? 'N/A'}</td>
                  <td>{question.topicLabel || question.topicId || 'N/A'}</td>
                  <td>{question.notAllowRecycle ? 'Disabled' : 'Allowed'}</td>
                  <td className="text-center">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => viewDetails(question.id)}>
                      View details
                    </button>
                  </td>
                </tr>
              ))}
              {questions.length === 0 && !loading ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    No questions found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {loading && <div className="text-muted small">Loading questions…</div>}
      </div>
      <div className="card-footer d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
        <div className="text-muted small">
          Showing page {hasItems ? page : 0} of {hasItems ? totalPages : 0} ({totalItems} questions)
        </div>
        <div className="btn-group" role="group" aria-label="Questions pagination">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setPage(1)}
            disabled={!hasItems || page === 1 || loading}
          >
            « First
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={!hasItems || page === 1 || loading}
          >
            ‹ Prev
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={!hasItems || page === totalPages || loading}
          >
            Next ›
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setPage(totalPages)}
            disabled={!hasItems || page === totalPages || loading}
          >
            Last »
          </button>
        </div>
      </div>

      {detailModal && (
        <QuestionDetailModal
          detail={detailModal.data ?? ({} as QuestionDetail)}
          loading={detailModal.loading}
          updating={detailModal.updating}
          error={detailModal.error}
          onClose={() => setDetailModal(null)}
          onToggleRecycle={toggleRecycle}
        />
      )}
    </div>
  );
}

type ChoiceKey = keyof QuestionDetail['choices'];
const CHOICE_KEYS: ChoiceKey[] = ['A', 'B', 'C', 'D'];

function QuestionDetailModal({ detail, loading, updating, error, onClose, onToggleRecycle }: QuestionDetailModalProps) {
  const hasData = Boolean(detail && detail.id);
  const correctChoice = detail?.correct_choice as ChoiceKey | undefined;
  return (
    <Modal
      title="Question details"
      onClose={onClose}
      size="lg"
      footer={
        hasData ? (
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={updating}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onToggleRecycle(detail, !detail.notAllowRecycle)}
              disabled={updating}
            >
              {updating ? 'Updating…' : detail.notAllowRecycle ? 'Allow recycle' : 'Disable recycle'}
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Close
          </button>
        )
      }
    >
      {loading && <div className="text-muted">Loading question…</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      {hasData && !loading && (
        <div className="d-flex flex-column gap-3">
          <div>
            <h5 className="fw-semibold">Prompt</h5>
            <pre className="bg-light p-3 rounded small text-wrap">{detail.prompt}</pre>
          </div>
          <div>
            <h6 className="fw-semibold">Choices</h6>
            <ul className="list-group">
              {CHOICE_KEYS.map((choice) => (
                <li
                  key={choice}
                  className={`list-group-item d-flex justify-content-between align-items-center ${
                    correctChoice === choice ? 'list-group-item-success' : ''
                  }`}
                >
                  <span>
                    <strong className="me-2">{choice}.</strong>
                    {detail.choices[choice]}
                  </span>
                  {correctChoice === choice && <span className="badge bg-success">Correct</span>}
                </li>
              ))}
            </ul>
          </div>
          <div className="small text-muted">
            <div>Topic: {detail.topicLabel || detail.topicId || 'N/A'}</div>
            <div>Difficulty: {detail.difficulty ?? 'N/A'}</div>
            <div>Generated: {formatDateTime(detail.created_at)}</div>
            <div>Status: {detail.notAllowRecycle ? 'Recycle disabled' : 'Recycle allowed'}</div>
          </div>
          {detail.recommended_steps && (
            <div>
              <h6 className="fw-semibold">Recommended steps</h6>
              <pre className="bg-light p-3 rounded small text-wrap">{detail.recommended_steps}</pre>
            </div>
          )}
          {detail.verification_notes && (
            <div>
              <h6 className="fw-semibold">Verification notes</h6>
              <pre className="bg-light p-3 rounded small text-wrap">{detail.verification_notes}</pre>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
